import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { createPopper } from "@popperjs/core";
import { cn } from "../../utils/utils";
import { COMMON } from "../../constants/commonText.constants";
import { useTran } from "../../hooks/useTran";

export function MySelect({
  className,
  selectClassName,
  label,
  error,
  options = [],
  onChange,
  value,
  placeholder = "Select an option",
  disabled,
  id,
  labelClassName,
  required,
  customT,
  searchable = true,
}) {
  const t = customT || useTran().t;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const chevronRef = useRef(null);
  const searchInputRef = useRef(null);
  const popperInstanceRef = useRef(null);

  const selectedOption = options.find((option) => option.key === value);

  // search algorithm
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase().trim();
    const results = [];

    // Score-based matching for better relevance
    const scoreOption = (option) => {
      const text = t(option.value).toLowerCase();
      const searchText = option.searchText?.toLowerCase() || "";

      // Exact match gets highest score
      if (text === query) return 1000;

      // Starts with query gets high score
      if (text.startsWith(query)) return 800;

      // Contains query as whole word gets medium score
      if (
        text.includes(` ${query} `) ||
        text.includes(` ${query}`) ||
        text.includes(`${query} `)
      ) {
        return 600;
      }

      // Contains query gets lower score
      if (text.includes(query)) return 400;

      // Search in additional search text
      if (searchText.includes(query)) return 300;

      // Fuzzy matching for typos (simple character distance)
      const fuzzyScore = calculateFuzzyScore(text, query);
      if (fuzzyScore > 0.7) return Math.floor(fuzzyScore * 200);

      return 0;
    };

    // Calculate simple fuzzy score based on character matching
    const calculateFuzzyScore = (text, query) => {
      if (query.length === 0) return 0;

      let matches = 0;
      let textIndex = 0;

      for (let i = 0; i < query.length; i++) {
        const char = query[i];
        const foundIndex = text.indexOf(char, textIndex);

        if (foundIndex !== -1) {
          matches++;
          textIndex = foundIndex + 1;
        }
      }

      return matches / query.length;
    };

    // Score and filter options
    for (const option of options) {
      const score = scoreOption(option);
      if (score > 0) {
        results.push({ ...option, _score: score });
      }
    }

    // Sort by score (highest first) and return top results
    return results.sort((a, b) => b._score - a._score);
  }, [options, searchQuery, t]);

  // Reset focused index when options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredOptions]);

  // Handle click outside and popper instance
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !chevronRef.current?.contains(event.target) &&
        !dropdownRef.current?.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    const createPopperInstance = () => {
      if (inputRef.current && dropdownRef.current) {
        popperInstanceRef.current = createPopper(
          inputRef.current,
          dropdownRef.current,
          {
            placement: "bottom-start",
            modifiers: [
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top-start"],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                },
              },
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ],
          },
        );
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      createPopperInstance();

      // Focus search input when dropdown opens
      if (searchable) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    } else if (popperInstanceRef.current) {
      popperInstanceRef.current.destroy();
      popperInstanceRef.current = null;
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, [isOpen, searchable]);

  const handleSelectOption = (option) => {
    if (option.disabled) return;

    const syntheticEvent = {
      target: {
        value: option.key,
        id,
      },
    };
    onChange?.(syntheticEvent);
    setIsOpen(false);
    setSearchQuery("");
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleSelectOption(filteredOptions[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        setFocusedIndex(-1);
        break;
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery("");
      }
    }
  };

  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark
          key={index}
          className="bg-yellow-200 text-yellow-900 px-0.5 rounded"
        >
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <div className={cn("space-y-1", className)} id={id}>
      {label && (
        <label
          className={cn("block font-medium text-gray-700", labelClassName)}
        >
          {t(label)}
          {required && <span className="text-error"> *</span>}
        </label>
      )}

      <div className="relative w-full">
        <div
          ref={inputRef}
          className={cn(
            "bg-white relative w-full h-10 border-border border rounded-md focus-within:border-gray-400 focus-within:bg-accent/50",
            error && "border-danger",
            disabled && "cursor-not-allowed opacity-70",
            selectClassName,
          )}
          onClick={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
        >
          <div className="flex items-center h-full px-3 cursor-pointer">
            <span
              className={cn(
                "flex-1 text-left text-nowrap  overflow-hidden",
                !selectedOption && "text-gray-400",
              )}
            >
              {selectedOption ? t(selectedOption.value) : t(placeholder)}
            </span>

            <button
              ref={chevronRef}
              type="button"
              className={cn(
                "ml-2 p-1 text-gray-500 hover:text-gray-700 rounded",
                disabled && "cursor-not-allowed",
              )}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(e);
              }}
              aria-label="Toggle dropdown"
              disabled={disabled}
            >
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform",
                  isOpen && "transform rotate-180",
                )}
              />
            </button>
          </div>
        </div>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="z-[300] w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            {searchable && (
              <div className="p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Search ${t(placeholder).toLowerCase()}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            )}

            <ul
              className="!ml-0 max-h-60 py-1 overflow-auto"
              role="listbox"
              id="options-list"
            >
              {filteredOptions.length === 0 ? (
                <li className="px-3 py-2 text-gray-500 text-center">
                  {searchQuery
                    ? `No results for "${searchQuery}"`
                    : t(COMMON.NO_OPTIONS_FOUND)}
                </li>
              ) : (
                filteredOptions.map((option, index) => (
                  <li
                    key={option.key}
                    className={cn(
                      "px-3 py-2 flex items-center justify-between transition-colors",
                      value === option.key
                        ? "bg-blue-100 text-blue-900"
                        : index === focusedIndex
                          ? "bg-gray-100"
                          : "hover:bg-gray-50",
                      option.disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer",
                    )}
                    onClick={() => handleSelectOption(option)}
                    role="option"
                    aria-selected={value === option.key}
                  >
                    <span className="flex-1">
                      {highlightMatch(t(option.value), searchQuery)}
                    </span>
                    {value === option.key && (
                      <Check className="w-4 h-4 text-blue-500 ml-2" />
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {error && <small className="text-error text-xs">{error}</small>}
    </div>
  );
}
